import axios from 'axios'
import clsx from 'clsx'
import {Loader2} from 'lucide-react'
import {signIn} from 'next-auth/react'
import Link from 'next/link'
import {useRouter, useSearchParams} from 'next/navigation'
import {useReducer, useState} from 'react'
import {overrideTailwindClasses} from 'tailwind-override'

import {Modal} from '@/components/modals/BaseModal'
import {Button} from '@/components/ui/button'

const TextInput = ({className = null, ...props}) => {
  const classes = overrideTailwindClasses(
    clsx(
      'h-10 w-full lg:max-w-[24rem] px-10 py-2 bg-white rounded-full lg:h-12 md:text-xl focus:outline-slate-500',
      className
    )
  )
  return <input className={classes} {...props}></input>
}

type InitialState = {
  firstName: string
  lastName: string
  email: string
  confirmEmail: string
  confirmEmailError: string
  password: string
  confirmPassword: string
  confirmPasswordError: string
  isSubmitting: boolean
}

type ReducerActions =
  | {type: 'firstName'; payload: string}
  | {type: 'lastName'; payload: string}
  | {type: 'email'; payload: string}
  | {type: 'confirmEmail'; payload: string}
  | {type: 'password'; payload: string}
  | {type: 'confirmPassword'; payload: string}
  | {type: 'submitting'}

const initialState: InitialState = {
  firstName: '',
  lastName: '',
  email: '',
  confirmEmail: '',
  confirmEmailError: '',
  password: '',
  confirmPassword: '',
  confirmPasswordError: '',
  isSubmitting: false,
}

const reducer = (state: InitialState, action: ReducerActions, emailConfirmation: boolean) => {
  switch (action.type) {
    case 'firstName':
      return {...state, firstName: action.payload}
    case 'lastName':
      return {...state, lastName: action.payload}
    case 'email':
      return validateEmail({...state, email: action.payload}, emailConfirmation)
    case 'confirmEmail':
      return validateEmail({...state, confirmEmail: action.payload}, emailConfirmation)
    case 'password':
      return validatePassword({...state, password: action.payload})
    case 'confirmPassword':
      return validatePassword({...state, confirmPassword: action.payload})
    case 'submitting':
      return {...state, isSubmitting: !state.isSubmitting}
    default:
      throw new Error()
  }
}

const validateEmail = (state: InitialState, emailConfirmation: Boolean) => {
  if (emailConfirmation && state.email !== state.confirmEmail) {
    return {...state, confirmEmailError: true}
  }
  return {...state, confirmEmailError: false}
}

const validatePassword = (state: InitialState) => {
  if (state.password !== state.confirmPassword) return {...state, confirmPasswordError: true}
  return {...state, confirmPasswordError: false}
}

const RegistrationErrorMsg = ({msg}: {msg: string}) => {
  return (
    <div className='p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800'>{msg}</div>
  )
}

export const DialogFooter = ({
  setIsOpen,
  emailConfirmation,
}: {
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  emailConfirmation: boolean
}) => {
  const router = useRouter()
  const handleCloseClick = () => {
    setIsOpen(false)
  }
  return (
    <div className='flex items-center justify-end pr-4 space-x-6'>
      <Button className='rounded-full' onClick={handleCloseClick}>
        Close
      </Button>
      {!emailConfirmation && (
        <Button
          className='text-slate-900 font-semibold border-none bg-gradient-to-r from-[#00FF8C] to-[#B8FF00] rounded-full'
          onClick={() => {
            router.push('/auth/login')
          }}
        >
          Sign up
        </Button>
      )}
    </div>
  )
}

export const RegistrationForm = ({
  setRegistrationStatus,
  setUserCreated,
  emailConfirmation = false,
}: {
  setRegistrationStatus: React.Dispatch<React.SetStateAction<boolean>>
  setUserCreated: React.Dispatch<React.SetStateAction<boolean>>
  emailConfirmation?: boolean
}) => {
  const [formState, dispatch] = useReducer<(state: InitialState, action: ReducerActions) => any>(
    (state, action) => reducer(state, action, emailConfirmation),
    initialState
  )
  const [registrationError, setRegistrationError] = useState('')
  const [disabledSubmit, setDisabledSubmit] = useState(true)
  const [isOpen, setIsOpen] = useState(false)

  const router = useRouter()

  const redirectTo = useSearchParams().get('redirectTo')
  const handleSubmit = async (e: any) => {
    e.preventDefault()

    if (disabledSubmit) {
      alert('Please agree to our Terms and Conditions and Privacy Policy')
      return false
    }

    dispatch({type: 'submitting'})

    const response = await axios.post('/api/auth/register/', {
      ...formState,
      ...(emailConfirmation && {emailConfirmation}),
    })

    dispatch({type: 'submitting'})

    if (response.data?.error) {
      if (response.data.error == 'E-mail address is already in use.') {
        setIsOpen(true)
        setRegistrationError(
          'An account with this email already exists. If you have already purchased courses on www.knowledgecoop.com, enter the same email and password used during checkout. '
        )
      } else {
        setRegistrationError(response.data.error)
      }
      setRegistrationStatus(false)
    } else {
      setRegistrationError('')
      setUserCreated(response.data?.user_created)
      setRegistrationStatus(true)

      if (response.data?.access_token && emailConfirmation) {
        dispatch({type: 'submitting'})
        await signIn('credentials', {
          email: formState.email,
          password: formState.password,
          domain: window.location.host,
          redirect: false,
        })
        if (redirectTo) {
          router.push(`/subscribe?redirectTo=${encodeURIComponent(redirectTo)}`)
        } else {
          router.push('/subscribe')
        }
      }
    }
  }
  return (
    <>
      {registrationError && (
        <Modal
          className='border-8 border-primary'
          open={isOpen}
          onClose={() => setIsOpen(false)}
          title=''
          body='An account with this email already exists. If you have already purchased courses on www.knowledgecoop.com, enter the same email and password used during checkout.'
          footer={<DialogFooter setIsOpen={setIsOpen} emailConfirmation={emailConfirmation} />}
        />
      )}
      <form
        className='flex flex-col items-center justify-center flex-1 w-full mt-10 space-y-2 md:space-y-4'
        onSubmit={handleSubmit}
      >
        <TextInput
          required
          type='text'
          placeholder='First Name'
          autoComplete='off'
          value={formState.firstName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => dispatch({type: 'firstName', payload: e.target.value})}
        />
        <TextInput
          required
          type='text'
          placeholder='Last Name'
          autoComplete='off'
          value={formState.lastName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => dispatch({type: 'lastName', payload: e.target.value})}
        />
        <TextInput
          required
          type='email'
          placeholder='Email'
          autoComplete='off'
          value={formState.email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => dispatch({type: 'email', payload: e.target.value})}
        />
        {emailConfirmation && (
          <TextInput
            required
            className={formState.confirmEmailError ? 'border-2 border-red-500' : ''}
            type='email'
            placeholder='Confirm Email'
            autoComplete='off'
            value={formState.confirmEmail}
            onChange={(e: any) => dispatch({type: 'confirmEmail', payload: e.target.value})}
          />
        )}
        <TextInput
          required
          type='password'
          placeholder='Password'
          autoComplete='off'
          value={formState.password}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => dispatch({type: 'password', payload: e.target.value})}
        />
        <TextInput
          required
          className={formState.confirmPasswordError ? 'border-2 border-red-500' : ''}
          type='password'
          placeholder='Confirm Password'
          autoComplete='off'
          value={formState.confirmPassword}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            dispatch({type: 'confirmPassword', payload: e.target.value})
          }
        />

        <div className='text-center'>
          <input
            type='checkbox'
            id='terms-checkbox'
            className='cursor-pointer'
            onChange={(e) => {
              setDisabledSubmit(!e.target.checked)
            }}
          ></input>
          <label htmlFor='terms-checkbox' className='text-sm text-slate-200'>
            &nbsp;By creating an account I agree to the Knowledge Coop&nbsp;
            <Link
              href='https://help.knowledgecoop.com/en/knowledge/knowledge-coop-terms-and-conditions'
              target='_blank'
              className='text-slate-400 hover:text-slate-100 hover:underline'
            >
              Terms and Conditions
            </Link>
            &nbsp;and&nbsp;
            <Link
              href='https://help.knowledgecoop.com/en/knowledge/knowledge-coop-privacy-policy'
              target='_blank'
              className='text-slate-400 hover:text-slate-100 hover:underline'
            >
              Privacy Policy
            </Link>
          </label>
        </div>

        <div className='flex flex-col items-center justify-center flex-1'>
          <div className='flex flex-col items-center m-auto space-x-2 space-y-2'>
            <Button
              type='submit'
              className='disabled:opacity-100 text-lg text-slate-900 font-semibold border-none bg-gradient-to-r from-[#00FF8C] to-[#B8FF00] inline-flex items-center justify-center rounded-full'
              disabled={formState.isSubmiting || disabledSubmit}
            >
              {formState.isSubmitting && <Loader2 className='w-4 h-4 mr-3 animate-spin' />}
              <span>Create Account</span>
            </Button>
            <span
              className='text-lg text-white border-none bolder hover:underline hover:cursor-pointer'
              onClick={() => {
                router.push('/auth/login')
              }}
            >
              Sign in
            </span>
          </div>
        </div>
      </form>
    </>
  )
}
